import { createClient, SupabaseClient } from '@supabase/supabase-js';

/**
 * Anonymous Supabase Client for Feedback Operations
 *
 * This client is specifically configured to operate in anonymous mode,
 * ensuring no authenticated session tokens are used even if a user is
 * logged in elsewhere in the app.
 *
 * WHY THIS EXISTS:
 * - Feedback forms should work for anyone with the link (anonymous access)
 * - When users are logged into the main app, their auth tokens persist in localStorage
 * - The default Supabase client automatically uses these tokens for ALL requests
 * - This causes "authenticated" role to be used instead of "anon" role
 * - Storage policies may behave differently for authenticated vs anonymous users
 * - This isolated client ensures true anonymous operation for feedback submissions
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

/**
 * Creates a fresh anonymous Supabase client with no session persistence
 */
export function createAnonymousClient(): SupabaseClient {
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      // Disable automatic session persistence
      persistSession: false,
      // Don't auto-refresh tokens (we don't have a session anyway)
      autoRefreshToken: false,
      // Don't detect session from URL (prevents token leakage)
      detectSessionInUrl: false,
      // Use a unique storage key to avoid conflicts with authenticated client
      storageKey: 'mentra-anonymous-feedback',
    },
    // Ensure we're using the anon key with no session
    global: {
      headers: {
        'X-Client-Info': 'mentra-anonymous-feedback',
      },
    },
  });
}

/**
 * Singleton anonymous client instance
 * Use this for all feedback-related operations
 */
export const anonymousSupabase = createAnonymousClient();

/**
 * Session Isolation Utilities
 */

/**
 * Checks if there's an authenticated session in the default Supabase client
 */
export async function hasAuthenticatedSession(): Promise<boolean> {
  const authData = localStorage.getItem('sb-zigrjilwxfjfwwknoohm-auth-token');
  return authData !== null;
}

/**
 * Gets diagnostic information about the current session state
 */
export async function getSessionDiagnostics(): Promise<{
  hasStoredAuth: boolean;
  storageKeys: string[];
  sessionCount: number;
}> {
  const storageKeys: string[] = [];
  const sessionCount = Object.keys(localStorage).filter(key => {
    if (key.includes('supabase') || key.includes('sb-')) {
      storageKeys.push(key);
      return true;
    }
    return false;
  }).length;

  return {
    hasStoredAuth: sessionCount > 0,
    storageKeys,
    sessionCount,
  };
}

/**
 * Clears any authenticated session state temporarily for feedback operations
 * Returns the cleared state so it can be restored if needed
 */
export function temporarilyClearAuthState(): Map<string, string> {
  const clearedState = new Map<string, string>();

  // Find all Supabase-related keys in localStorage
  const keysToTemp = Object.keys(localStorage).filter(
    key => key.includes('supabase') || key.includes('sb-')
  );

  // Store them temporarily
  keysToTemp.forEach(key => {
    const value = localStorage.getItem(key);
    if (value) {
      clearedState.set(key, value);
    }
  });

  console.log('[AnonymousClient] Temporarily cleared auth state:', {
    keysCleared: clearedState.size,
    keys: Array.from(clearedState.keys()),
  });

  return clearedState;
}

/**
 * Restores previously cleared auth state
 */
export function restoreAuthState(state: Map<string, string>): void {
  state.forEach((value, key) => {
    localStorage.setItem(key, value);
  });

  console.log('[AnonymousClient] Restored auth state:', {
    keysRestored: state.size,
  });
}

/**
 * Validates that we're truly in anonymous mode
 */
export async function validateAnonymousMode(client: SupabaseClient): Promise<{
  isAnonymous: boolean;
  role: string;
  hasSession: boolean;
}> {
  const { data: { session } } = await client.auth.getSession();

  return {
    isAnonymous: !session,
    role: session ? 'authenticated' : 'anon',
    hasSession: !!session,
  };
}
